import type { MutableRefObject } from 'react';
import { combineLatest, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConnectionStatusEnum, type CurrencyConversionRate, type Security } from '../types';
import { isCFD, isFuture, isMultileg, isPerpetualSwap, isSpot } from '../utils';
import { useHomeCurrencyRate } from './useHomeCurrencyRate';
import { useObservableRef, useObservableValue } from './useObservable';

function selectConversionRate({
  currency,
  currencyRate,
  security,
  symbolRate,
}: {
  currency: string | undefined;
  currencyRate: CurrencyConversionRate | undefined;
  security: Security | undefined;
  symbolRate: CurrencyConversionRate | undefined;
}): CurrencyConversionRate | undefined {
  // Prioritize Currency Conversion for ProductTypeEnum.Spot, Multileg, Perps or Futures for non-contract orders (currency is set) and CFDs
  if (
    currency &&
    (isSpot(security) || isMultileg(security) || isPerpetualSwap(security) || isFuture(security) || isCFD(security))
  ) {
    return currencyRate;
  }
  return symbolRate?.Status === ConnectionStatusEnum.Online ? symbolRate : currencyRate;
}

function getConversionObservable({
  currency,
  currencyConversion,
  security,
  symbolConversion,
}: {
  currency: string | undefined;
  currencyConversion: Observable<CurrencyConversionRate | undefined>;
  security: Security | undefined;
  symbolConversion: Observable<CurrencyConversionRate | undefined>;
}) {
  return combineLatest([symbolConversion, currencyConversion]).pipe(
    map(([symbolRate, currencyRate]) => selectConversionRate({ currency, currencyRate, security, symbolRate }))
  );
}

/**
 * Returns the latest conversion in a ref without re-rendering when rates update.
 * Use this for imperative reads where the caller already controls when to recalculate.
 */
export function useConversionRef(
  currency: string | undefined,
  symbol: string | undefined,
  security: Security | undefined
): MutableRefObject<CurrencyConversionRate | undefined> {
  const currencyConversion = useHomeCurrencyRate(currency);
  const symbolConversion = useHomeCurrencyRate(symbol);

  return useObservableRef(
    () =>
      getConversionObservable({
        currency,
        currencyConversion,
        security,
        symbolConversion,
      }),
    [symbolConversion, currencyConversion, security, currency]
  );
}

/**
 * Returns the latest conversion as React state, re-rendering when rates update.
 * Use this when effects, memoized callbacks, or rendered output must react to late subscription emissions.
 */
export function useConversionValue(
  currency: string | undefined,
  symbol: string | undefined,
  security: Security | undefined
): CurrencyConversionRate | undefined {
  const currencyConversion = useHomeCurrencyRate(currency);
  const symbolConversion = useHomeCurrencyRate(symbol);

  return useObservableValue(
    () =>
      getConversionObservable({
        currency,
        currencyConversion,
        security,
        symbolConversion,
      }),
    [symbolConversion, currencyConversion, security, currency]
  );
}
